<template>
  <div>
    <v-form ref="form" v-model="valid">
      <v-card :loading="saving ? 'secondary' : false">
        <v-card-title v-if="isOnForm" class="primary white--text mb-6">
          <v-icon dark class="mr-2">{{ titleIcon }}</v-icon>
          {{ title }}
        </v-card-title>
        <v-card-text class="px-5 pb-6">
          <v-expand-transition>
            <div>
              <v-row>
                <v-col cols="12" class="px-0 pt-0 pb-2">
                  <v-card-title class="py-0 text-subtitle-1 font-weight-bold">
                    <v-icon class="mr-2 primary--text">mdi-account</v-icon>
                    {{ $t("components.dependants_form.basic_information") }}
                  </v-card-title>
                </v-col>
                <v-col cols="12" md="6" class="py-0">
                  <no-autocomplete-textfield
                    outlined
                    clearable
                    :label="$t('components.beneficiary_form.first_name')"
                    v-model.trim="internalDependant.FirstName"
                    :rules="[$rules.required, $rules.validName]"
                    :tabindex="1"
                    class="required"
                  ></no-autocomplete-textfield>
                </v-col>
                <v-col cols="12" md="6" class="py-0">
                  <no-autocomplete-textfield
                    outlined
                    clearable
                    :label="$t('components.beneficiary_form.last_name')"
                    v-model.trim="internalDependant.LastName"
                    :rules="[$rules.required, $rules.validLastName]"
                    :tabindex="2"
                    class="required"
                  ></no-autocomplete-textfield>
                </v-col>
                <v-col cols="12" md="6" class="py-0">
                  <v-autocomplete
                    outlined
                    :label="$t('components.beneficiary_form.relationship')"
                    :items="relationships"
                    v-model="internalDependant.Relationship"
                    @change="checkRequiredProofs"
                    :rules="[$rules.required]"
                    :tabindex="3"
                    return-object
                    item-value="Id"
                    :item-text="
                      (item) => $helpers.translate(item, $vuetify.lang.current)
                    "
                    class="required"
                  ></v-autocomplete>
                </v-col>
                <v-col cols="12" md="6" class="py-0">
                  <v-autocomplete
                    outlined
                    :label="$t('components.beneficiary_form.gender')"
                    v-model="internalDependant.Gender"
                    :items="enumModels.Gender"
                    :tabindex="4"
                    :rules="[$rules.required]"
                    item-value="value"
                    :item-text="
                      $vuetify.lang.current === 'en' ? 'textEn' : 'textFr'
                    "
                    return-object
                    class="required"
                  ></v-autocomplete>
                </v-col>

                <v-col cols="12" md="6" class="py-0">
                  <v-menu
                    v-model="dateMenu"
                    :close-on-content-click="false"
                    :z-index="1000"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        clearable
                        v-mask="'####-##-##'"
                        outlined
                        v-model="internalDependant.DateOfBirth"
                        :tabindex="5"
                        :label="$t('components.beneficiary_form.date_of_birth')"
                        append-icon="mdi-calendar"
                        @click:append="dateMenu = true"
                        @input="
                          internalDependant.DateOfBirth?.length > 3
                            ? (pickerDate = internalDependant.DateOfBirth)
                            : ''
                        "
                        v-bind="attrs"
                        v-on="on"
                        :rules="[
                          $rules.required,
                          $rules.validDate,
                          $rules.dateOfBirth,
                        ]"
                        class="required"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="internalDependant.DateOfBirth"
                      :max="$helpers.toISOStringLocal(new Date())"
                      @input="dateMenu = false"
                      :picker-date.sync="pickerDate"
                    ></v-date-picker>
                  </v-menu>
                </v-col>

                <v-col cols="12" md="6" class="py-0">
                  <v-select
                    v-model="internalDependant.CoverageGroups"
                    :tabindex="5"
                    :items="coverageGroups"
                    item-text="Name"
                    item-value="Id"
                    return-object
                    outlined
                    :label="$t('components.beneficiary_form.coverage_groups')"
                    class="required"
                    multiple
                    :rules="[(v) => (v && v.length > 0) || 'Required']"
                    @click="selectingCoverage = true"
                    @blur="selectingCoverage = false"
                  />
                </v-col>
              </v-row>
              <v-row
                :style="{
                  marginTop: selectingCoverage ? '220px' : '16px',
                  transition: 'margin-top 0.2s ease',
                }"
              >
                <v-col
                  v-if="
                    internalDependant.CoverageGroups &&
                    internalDependant.CoverageGroups.length &&
                    validDependantPeriod
                  "
                  cols="12"
                  class="d-flex justify-center"
                >
                  <p
                    class="font-weight-bold primary--text text-h6 mb-0 text-center"
                  >
                    {{ $t("components.dependant_form.totalAmount") }} ${{
                      owedAmount.toFixed(2)
                    }}
                  </p>
                </v-col>
              </v-row>
              <v-row>
                <v-col
                  xs="12"
                  sm="6"
                  md="4"
                  lg="4"
                  class="chekbox-no-margin pt-0 pt-6 pb-0 mt-n2 mb-4"
                  v-if="
                    internalDependant?.Relationship?.IsChild &&
                    !internalDependant?.Relationship?.IsSpouse
                  "
                >
                  <v-checkbox
                    v-model="internalDependant.Disabled"
                    :label="$t('components.dependants_form.disabled')"
                    @change="removeProof($event, 'DependantDisabilityProof')"
                  ></v-checkbox>
                </v-col>

                <v-col
                  xs="12"
                  sm="6"
                  md="6"
                  lg="6"
                  v-if="internalDependant?.Relationship?.IsSpouse"
                  class="chekbox-no-margin pt-0 pt-6 pb-0 mt-n2 mb-4"
                >
                  <v-checkbox
                    v-show="validDependantPeriod.IsSmokerPrompt"
                    v-model="internalDependant.IsSmoker"
                    :label="$t('components.dependants_form.smoker')"
                  ></v-checkbox>
                </v-col>
              </v-row>
              <v-row>
                <v-col
                  v-if="internalDependant?.Relationship?.IsChild"
                  cols="12"
                  class="chekbox-no-margin pt-0 pt-6 pb-0 mt-n2 mb-4"
                >
                  <v-checkbox
                    v-model="internalDependant.Student"
                    :label="$t('components.dependants_form.student')"
                    @change="removeProof($event, 'DependantSchoolingProof')"
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col
                  cols="12"
                  md="6"
                  class="py-0"
                  v-if="internalDependant?.Student"
                >
                  <v-menu
                    v-model="dateMenu3"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                    :z-index="1000"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        clearable
                        :tabindex="6"
                        v-mask="'####-##-##'"
                        outlined
                        v-model="internalDependant.SchoolCompletionDate"
                        :label="
                          $t(
                            'components.beneficiary_form.school_completion_date'
                          )
                        "
                        append-icon="mdi-calendar"
                        @click:append="
                          dateMenu3 = true;
                          pickerDate =
                            internalDependant.SchoolCompletionDate?.length > 3
                              ? internalDependant.SchoolCompletionDate
                              : $helpers.toISOStringLocal(new Date());
                        "
                        @input="
                          internalDependant.SchoolCompletionDate?.length > 3
                            ? (pickerDate =
                                internalDependant.SchoolCompletionDate)
                            : ''
                        "
                        v-bind="attrs"
                        v-on="on"
                        :rules="[
                          $rules.required,
                          $rules.validDate,
                          $rules.schoolDate,
                        ]"
                        class="required"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="internalDependant.SchoolCompletionDate"
                      :min="$helpers.toISOStringLocal(new Date())"
                      @input="dateMenu3 = false"
                      :picker-date.sync="schoolPickerDate"
                    ></v-date-picker>
                  </v-menu>
                </v-col>
              </v-row>
              <v-row>
                <add-proofs
                  v-if="proofRequired && requireProofs"
                  :dependant.sync="internalDependant"
                  :clearProofs="clearProofs"
                  :proofToRemove="proofToRemove"
                  :requiresProof="requireProofs"
                  @save="saveDependantProof"
                  @cancel="cancelDependantProofDialog"
                  @remove-receipt="uploadProof(false, $event)"
                  :rules="proofRules"
                  :class="{ required: requireProofs }"
                ></add-proofs>
              </v-row>
              <v-row
                class="mt-0"
                v-if="
                  internalDependant?.IsNew && internalDependant?.Relationship
                "
              >
                <v-col cols="12" class="pa-0 py-2">
                  <v-card-title class="py-0 text-subtitle-1 font-weight-bold">
                    <v-icon class="mr-2 primary--text"
                      >mdi-hand-heart-outline</v-icon
                    >
                    {{ $t("components.dependants_form.coverage_information") }}
                  </v-card-title>
                </v-col>
                <v-col cols="12" class="chekbox-no-margin pt-0 pb-2">
                  <v-checkbox
                    v-model="internalDependant.HasCOB"
                    :label="
                      $t(
                        internalDependant?.Relationship?.IsSpouse
                          ? 'components.dependants_form.cob_spouse_label'
                          : 'components.dependants_form.cob_child_label'
                      )
                    "
                  ></v-checkbox>
                </v-col>
                <template v-if="internalDependant.HasCOB">
                  <v-col cols="12" md="4" class="py-0">
                    <no-autocomplete-textfield
                      outlined
                      :label="$t('components.dependants_form.name_of_insurer')"
                      v-model="internalDependant.NameOfInsurer"
                      :rules="[$rules.required]"
                      class="required"
                    ></no-autocomplete-textfield>
                  </v-col>
                  <v-col cols="12" md="4" class="py-0">
                    <v-menu
                      v-model="dateMenu2"
                      :close-on-content-click="false"
                      transition="scale-transition"
                      offset-y
                      min-width="auto"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          clearable
                          v-mask="'####-##-##'"
                          outlined
                          v-model="internalDependant.CobEffectiveDate"
                          :label="$t('globals.effective_date')"
                          append-icon="mdi-calendar"
                          @click:append="dateMenu2 = true"
                          @input="
                            internalDependant.CobEffectiveDate?.length > 3
                              ? (pickerDate =
                                  internalDependant.CobEffectiveDate)
                              : ''
                          "
                          v-bind="attrs"
                          v-on="on"
                          :rules="[$rules.required, $rules.validDate]"
                          class="required"
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="internalDependant.CobEffectiveDate"
                        @input="dateMenu2 = false"
                      ></v-date-picker>
                    </v-menu>
                  </v-col>
                  <v-col cols="12" md="4" class="py-0">
                    <no-autocomplete-textfield
                      outlined
                      :label="$t('components.dependants_form.policy_number')"
                      v-model="internalDependant.PolicyNumber"
                      class="required"
                    ></no-autocomplete-textfield>
                  </v-col>
                  <v-col cols="12" md="4" class="py-0">
                    <v-autocomplete
                      outlined
                      :label="$t('components.dependants_form.health')"
                      v-model="internalDependant.Health"
                      :items="enumModels?.FamilyStatus"
                      :rules="[$rules.required]"
                      item-value="value"
                      :item-text="
                        $vuetify.lang.current === 'en' ? 'textEn' : 'textFr'
                      "
                      class="required"
                    ></v-autocomplete>
                  </v-col>
                  <v-col cols="12" md="4" class="py-0">
                    <v-autocomplete
                      outlined
                      :label="$t('components.dependants_form.dental')"
                      v-model="internalDependant.Dental"
                      :items="enumModels?.FamilyStatus"
                      :rules="[$rules.required]"
                      item-value="value"
                      :item-text="
                        $vuetify.lang.current === 'en' ? 'textEn' : 'textFr'
                      "
                      class="required"
                    ></v-autocomplete>
                  </v-col>
                  <v-col cols="12" md="4" class="py-0">
                    <v-autocomplete
                      outlined
                      :label="$t('components.dependants_form.vision_care')"
                      v-model="internalDependant.VisionCare"
                      :items="enumModels?.FamilyStatus"
                      :rules="[$rules.required]"
                      item-value="value"
                      :item-text="
                        $vuetify.lang.current === 'en' ? 'textEn' : 'textFr'
                      "
                      class="required"
                    ></v-autocomplete>
                  </v-col>
                </template>
              </v-row>
              <!-- Signature -->
              <v-row
                no-gutters
                justify="space-around"
                align="center"
                v-if="validToSave && !isRegistrationCompletion"
              >
                <v-col cols="12" class="red--text font-weight-bold pt-0 pb-2">
                  {{
                    $t(
                      "components.contact_information_expansion_panel.signatutre_warning"
                    )
                  }}
                </v-col>
                <signature-input
                  :member="member"
                  @update-signature="signature = $event"
                />
                <v-col cols="12" md="4" class="pt-6 pb-4">
                  <v-text-field
                    outlined
                    :label="$t('components.beneficiary_form.designation_date')"
                    append-icon="mdi-calendar"
                    :value="$helpers.formattedDateNow()"
                    readonly
                  ></v-text-field>
                </v-col>
              </v-row>
            </div>
          </v-expand-transition>
        </v-card-text>
        <v-card-actions class="pa-4 pt-0 justify-end">
          <v-btn class="mr-3" @click="cancel">{{ $t("globals.cancel") }}</v-btn>
          <v-btn
            :loading="saving"
            color="primary"
            @click="save"
            :disabled="
              isRegistrationCompletion
                ? !validToSave
                : !signature || !validToSave
            "
          >
            {{ $t("globals.save") }}
          </v-btn>
        </v-card-actions>
        <coverage-groups-pricing-table
          class="ml-5"
          v-if="validDependantPeriod && !isOnForm"
          :dependant-settings="dependantSettings"
        ></coverage-groups-pricing-table>
      </v-card>
    </v-form>
  </div>
</template>

<script>
import NoAutocompleteTextfield from "@/components/controls/NoAutocompleteTextfield.vue";
import SignatureInput from "@/components/VueMaterialDashboard/SignatureInput.vue";
import AddProofs from "@/components/MemberInformation/items/panels/DependantsPanel/AddProofs.vue";
import { mapActions, mapGetters } from "vuex";
import CoverageGroupsPricingTable from "./CoverageGroupsPricingTable.vue";

export default {
  components: {
    NoAutocompleteTextfield,
    AddProofs,
    SignatureInput,
    CoverageGroupsPricingTable,
  },
  props: {
    dependantSettings: Object,
    member: Object,
    dependant: Object,
    minEffectiveDate: String,
    title: String,
    titleIcon: String,
    saving: Boolean,
    dependantSaved: Boolean,
    isRegistrationCompletion: Boolean,
    remainingPercentage: Number,
    dependants: Array,
    coverageGroups: Array,
    validDependantPeriod: Object,
    requireProofs: Boolean,
    prices: Array,
    isOnForm: { Boolean, default: true },
  },
  data() {
    return {
      internalDependant: {},
      internalRelationships: null,
      valid: false,
      dateMenu: false,
      dateMenu1: false,
      dateMenu2: false,
      dateMenu3: false,
      pickerDate: "2000-06",
      schoolPickerDate: "2027-6",
      uploadProofDialog: false,
      proofCategory: null,
      clearProofs: false,
      signature: null,
      proofToRemove: null,
      coverageGroupsSelected: [],
      owedAmount: 0,
      selectingCoverage: false,
      newDependant: {
        FirstName: "",
        LastName: "",
        Relationship: "",
        DateOfBirth: "",
        Gender: "",
        SchoolCompletionDate: "",
        coverageGroup: "",
      },
    };
  },
  computed: {
    ...mapGetters("auth", ["user"]),
    ...mapGetters("common", ["enumModels"]),
    canAddSpouse() {
      const isSpouseDeps = (this.dependants || []).filter((x) => {
        const relationship = this.enumModels?.Relationship?.find((y) => {
          const id = x.Relationship?.Id ? x.Relationship?.Id : x.Relationship;
          return y.Id?.toUpperCase() === id?.toUpperCase();
        });
        return (
          relationship?.IsSpouse && !x.IsRemoved && x.Id !== this.dependant?.Id
        );
      });
      return isSpouseDeps.length === 0;
    },
    pricingHeaders() {
      return [
        {
          text: this.$t("components.beneficiary_form.coverage_groups"),
          align: "center",
          sortable: false,
          value: "Name",
          cellClass: "text-subtitle-2",
          class: "secondary--text text-uppercase text-subtitle-2 name-heading",
        },
        {
          text: this.$t("components.dependant_form.amounts"),
          align: "center",
          sortable: false,
          value: "amount",
          cellClass: "text-subtitle-2",
          class: "secondary--text text-uppercase text-subtitle-2 name-heading",
        },
      ];
    },
    formattedCoverageGroups() {
      return this.pricingItems.map((p) => {
        let amount = "";

        if (p.Amount != null && p.PricingType === "Fixed") {
          amount = `${this.$t(
            "components.dependant_form.fixedType"
          )} $${p.Amount.toFixed(2)}`;
        } else if (p.Amount != null && p.PricingType === "Family") {
          amount = `${this.$t(
            "components.dependant_form.familyType"
          )} $${p.Amount.toFixed(2)}`;
        } else if (
          Array.isArray(p.Amounts) &&
          p.Amounts.length > 0 &&
          p.PricingType === "Variable"
        ) {
          amount = p.Amounts.filter((item) => item && item.Amount != null)
            .sort((a, b) => a.DependantNumber - b.DependantNumber)
            .map(
              (item) =>
                `${item.DependantNumber} ${this.$t(
                  "components.dependant_form.variableType"
                )} $${item.Amount.toFixed(2)}`
            )
            .join("\n");
        }

        return {
          Id: p.CoverageGroup?.Id,
          Name: p.CoverageGroup?.Name,
          Amount: p.Amount,
          Amounts: p.Amounts || [],
          PricingType: p.PricingType,
          amount,
        };
      });
    },
    totalOwedAmount() {
      var amount = 0;
      this.formattedCoverageGroups.forEach((cg) => {
        if (cg.Amount && cg.PricingType !== "Variable") {
          amount += cg.Amount;
        } else if (
          cg.Amounts &&
          Array.isArray(cg.Amounts) &&
          cg.Amounts.length &&
          cg.PricingType === "Variable"
        ) {
          amount += cg.Amounts.find((a) => a.DependantNumber === 1)?.Amount;
        }
      });
      return amount;
    },
    relationships() {
      if (this.dependant?.IsNew && this.canAddSpouse) {
        return this.enumModels?.Relationship;
      }
      if (this.currentDependatRelationship?.IsSpouse) {
        return this.enumModels?.Relationship?.filter((x) => x.IsSpouse);
      }
      if (this.currentDependatRelationship?.IsChild) {
        return this.enumModels?.Relationship?.filter((x) => x.IsChild);
      }
      if (this.canAddSpouse || this.internalDependant?.Relationship?.IsSpouse) {
        return this.internalRelationships;
      } else
        return this.internalRelationships.map((x) => {
          x.IsSpouse ? (x.disabled = true) : null;
          return x;
        });
      // } else return this.enumModels?.Relationship?.filter((x) => x.IsChild);
    },
    currentDependatRelationship() {
      const relationshipId = this.internalDependant?.Relationship?.Id
        ? this.internalDependant?.Relationship?.Id
        : this.internalDependant?.Relationship;
      return this.enumModels?.Relationship?.find((x) => {
        return x.Id?.toUpperCase() === relationshipId?.toUpperCase();
      });
    },
    proofRequired() {
      return (
        this.internalDependant.Student ||
        this.internalDependant.Disabled ||
        this.internalDependant.CommonLawProof ||
        this.internalDependant.LifeEventProof
      );
    },
    validToSave() {
      if (!this.requireProofs) {
        return this.valid;
      }

      const validStudent = this.internalDependant.Student
        ? !!this.internalDependant.Qualification?.ProofOfSchooling
        : true;
      const validDisabled = this.internalDependant.Disabled
        ? !!this.internalDependant.Qualification?.ProofOfDisability
        : true;
      const validCommonLaw = this.internalDependant.CommonLawProof
        ? !!this.internalDependant.Qualification?.ProofOfCommonLawStatus
        : true;
      const validLifeEvent = this.internalDependant.LifeEventProof
        ? !!this.internalDependant.Qualification?.ProofOfLifeEvent
        : true;
      return (
        validStudent &&
        validDisabled &&
        validCommonLaw &&
        validLifeEvent &&
        this.valid
      );
    },
    proofRules() {
      return this.requireProofs
        ? [(v) => (v && v.length > 0) || "Required"]
        : [];
    },
  },
  watch: {
    "internalDependant.CoverageGroups": {
      handler(v) {
        if (v === undefined || v.length === 0) {
          this.owedAmount = 0;
          return;
        }
        this.getCalculatedOwedAmount({
          payload: {
            Id: this.internalDependant.Id,
            Certificate: this.member?.Certificate ?? this.user.gatewayMemberId,
            PeriodId: this.validDependantPeriod.Id,
            PlanId: this.member?.PlanId ?? this.user.plan,
            CoverageGroups: v.map((cg) => ({
              Id: cg.Id,
              Name: cg.Name,
            })),
            Transactions: [],
          },
        }).then((response) => {
          this.owedAmount = response.data;
        });
      },
    },
    "internalDependant.Disabled"(v) {
      if (!v) {
        delete this.internalDependant?.Qualification?.ProofOfDisability;

        this.proofToRemove = "DependantDisabilityProof";
        this.$nextTick(() => {
          this.proofToRemove = null;
        });
      }
    },
    "internalDependant.Student"(v) {
      if (!v) {
        this.internalDependant.SchoolCompletionDate = null;
        delete this.internalDependant?.Qualification?.ProofOfSchooling;

        this.proofToRemove = "DependantSchoolingProof";
        this.$nextTick(() => {
          this.proofToRemove = null;
        });
      }
    },
    dependantSaved: {
      handler() {
        if (this.dependantSaved && !this.saving) {
          this.cancel();
        }
      },
      deep: true,
    },
    dependant: {
      handler() {
        this.internalDependant = JSON.parse(JSON.stringify(this.dependant));
        const relationshipId =
          typeof this.dependant?.Relationship === "string"
            ? this.dependant.Relationship
            : this.dependant?.Relationship?.Id;
        if (relationshipId) {
          const relationship = this.enumModels?.Relationship?.find(
            (x) => x.Id?.toUpperCase() === relationshipId?.toUpperCase()
          );
          if (relationship) this.internalDependant.Relationship = relationship;
        }
        this.internalDependant.SchoolCompletionDate =
          this.dependant?.SchoolCompletionDate?.split("T")[0] || "";
        this.internalDependant.DateOfBirth =
          this.dependant?.DateOfBirth?.split("T")[0];
        if (typeof this.dependant?.Gender === "string") {
          const gender = this.enumModels?.Gender?.find(
            (g) =>
              g.value?.toUpperCase() === this.dependant.Gender?.toUpperCase() ||
              g.textEn?.toUpperCase() === this.dependant.Gender?.toUpperCase()
          );
          if (gender) this.internalDependant.Gender = gender;
        }
        this.$refs.form?.resetValidation();
        this.internalRelationships = JSON.parse(
          JSON.stringify(this.enumModels?.Relationship)
        );
        this.internalDependant.Student = this.dependant?.Student;
        this.internalDependant.Disabled = this.dependant?.Disabled;
        this.checkRequiredProofs();
        this.clearProofs = false;
      },
      immediate: true,
    },
    "internalDependant.Relationship": {
      handler(v) {
        if (!v || !this.internalDependant) return;

        const qualification = this.internalDependant.Qualification;
        if (!qualification) return;

        if (v.IsChild) {
          if (
            this.internalDependant.Qualification.ProofOfCommonLawStatus &&
            this.internalDependant.Qualification.ProofOfCommonLawStatus !==
              undefined
          )
            this.internalDependant.Qualification.ProofOfCommonLawStatus = null;
        }
        if (v.IsSpouse) {
          if (
            this.internalDependant.Qualification.ProofOfSchooling &&
            this.internalDependant.Qualification.ProofOfSchooling !== undefined
          )
            this.internalDependant.Qualification.ProofOfSchooling = null;

          if (
            this.internalDependant.Qualification.ProofOfDisability &&
            this.internalDependant.Qualification.ProofOfDisability !== undefined
          )
            this.internalDependant.Qualification.ProofOfDisability = null;

          if (
            this.internalDependant.Qualification.ProofOfLifeEvent &&
            this.internalDependant.Qualification.ProofOfLifeEvent !== undefined
          ) {
            this.internalDependant.Qualification.ProofOfLifeEvent = null;
          }
          this.internalDependant.Student = false;
          this.internalDependant.Disabled = false;
        }
      },
      deep: true,
    },
    coverageGroupsSelected: {
      handler(v) {
        this.$emit("coverage-groups-selected", v);
      },
      deep: true,
    },
  },
  mounted() {
    console.log("dependant on form", this.dependant);
    console.log("dependant period on form", this.validDependantPeriod);
  },
  methods: {
    ...mapActions("data", ["getCalculatedOwedAmount"]),
    cancel() {
      this.$emit("cancel-dialog");
      this.internalDependant = {};
      this.uploadProofDialog = false;
      this.$refs.form.reset();
      this.$refs.form.resetValidation();
    },
    uploadProof(isTrue, proofCategory) {
      if (!isTrue) {
        if (proofCategory === "DependantCommonLawStatusProof") {
          delete this.internalDependant?.Qualification?.ProofOfCommonLawStatus;
        }
        if (proofCategory === "DependantLifeEventProof") {
          delete this.internalDependant?.Qualification?.ProofOfLifeEvent;
        }
        if (proofCategory === "DependantDisabilityProof") {
          delete this.internalDependant?.Qualification?.ProofOfDisability;
        }
        if (proofCategory === "DependantSchoolingProof") {
          delete this.internalDependant?.Qualification?.ProofOfSchooling;
        }
        this.internalDependant = JSON.parse(
          JSON.stringify(this.internalDependant)
        );
        return;
      }
      this.proofCategory = proofCategory;
      this.uploadProofDialog = true;
    },
    save() {
      this.uploadProofDialog = false;
      const dependant = JSON.parse(JSON.stringify(this.internalDependant));
      const dependantRelationship = dependant.Relationship;
      dependant.Relationship = dependant.Relationship?.Id
        ? dependant.Relationship?.Id
        : dependant.Relationship;
      dependant.RelationshipName = dependantRelationship?.Name || null;
      dependant.GenderId = dependant.Gender?.value || null;
      dependant.Gender = dependant.Gender?.textEn || dependant.Gender || "";
      dependant.Signature = this.signature;
      if (!dependant.Student) {
        delete dependant?.Qualification?.ProofOfSchooling;
      }
      if (!dependant.Disabled) {
        delete dependant?.Qualification?.ProofOfDisability;
      }
      this.$emit("save-dependant", dependant);
      this.clearProofs = true;
    },
    saveDependantProof(proof) {
      console.log("saveDependantProof", proof);
      console.log("BEFORE ASSIGNING PROOF", {
        disability: this.internalDependant?.Qualification?.ProofOfDisability,
        schooling: this.internalDependant?.Qualification?.ProofOfSchooling,
        lifeEvent: this.internalDependant?.Qualification?.ProofOfLifeEvent,
        commonLaw:
          this.internalDependant?.Qualification?.ProofOfCommonLawStatus,
      });
      if (!this.internalDependant?.Qualification) {
        this.internalDependant.Qualification = {};
      }
      if (proof?.ProofType === "DependantCommonLawStatusProof") {
        this.internalDependant.Qualification.ProofOfCommonLawStatus = proof;
      } else if (proof?.ProofType === "DependantDisabilityProof") {
        this.internalDependant.Qualification.ProofOfDisability = proof;
      } else if (proof?.ProofType === "DependantSchoolingProof") {
        this.internalDependant.Qualification.ProofOfSchooling = proof;
      } else if (proof?.ProofType === "DependantLifeEventProof") {
        this.internalDependant.Qualification.ProofOfLifeEvent = proof;
      }

      console.log("AFTER ASSIGNING PROOF, BEFORE CLONE", {
        disability: this.internalDependant?.Qualification?.ProofOfDisability,
        schooling: this.internalDependant?.Qualification?.ProofOfSchooling,
        lifeEvent: this.internalDependant?.Qualification?.ProofOfLifeEvent,
        commonLaw:
          this.internalDependant?.Qualification?.ProofOfCommonLawStatus,
      });
      this.uploadProofDialog = false;
      this.internalDependant = JSON.parse(
        JSON.stringify(this.internalDependant)
      );

      console.log("AFTER CLONE", {
        disability: this.internalDependant?.Qualification?.ProofOfDisability,
        schooling: this.internalDependant?.Qualification?.ProofOfSchooling,
        lifeEvent: this.internalDependant?.Qualification?.ProofOfLifeEvent,
        commonLaw:
          this.internalDependant?.Qualification?.ProofOfCommonLawStatus,
      });
    },
    cancelDependantProofDialog() {
      this.uploadProofDialog = false;
      switch (this.proofCategory) {
        case "DependantDisabilityProof":
          this.internalDependant.Disabled = false;
          break;
        case "DependantSchoolingProof":
          this.internalDependant.Student = false;
          break;
        case "DependantCommonLawStatusProof":
          this.internalDependant.CommonLawProof = false;
          break;
        case "DependantLifeEventProof":
          this.internalDependant.LifeEventProof = false;
          break;
      }
      this.proofCategory = null;
    },
    removeProof(doNotRemove, type) {
      if (doNotRemove) return;
      // if (!this.internalDependant?.Qualification) {
      //   this.internalDependant.Qualification = {};
      // }
      switch (type) {
        case "DependantDisabilityProof":
          delete this.internalDependant?.Qualification?.ProofOfDisability;
          break;
        case "DependantSchoolingProof":
          delete this.internalDependant?.Qualification?.ProofOfSchooling;
          break;
        case "DependantCommonLawStatusProof":
          delete this.internalDependant?.Qualification?.ProofOfCommonLawStatus;
          break;
        case "DependantLifeEventProof":
          delete this.internalDependant?.Qualification?.ProofOfLifeEvent;
          break;
      }
      this.internalDependant = JSON.parse(
        JSON.stringify(this.internalDependant)
      );
    },
    validate() {
      this.$refs.form?.validate();
    },
    checkRequiredProofs() {
      if (this.internalDependant?.Relationship?.IsSpouse) {
        this.internalDependant.CommonLawProof = true;
        this.internalDependant.Disabled = false;
        this.internalDependant.Student = false;
      } else {
        this.internalDependant.CommonLawProof = false;
      }
      if (this.internalDependant?.Relationship?.IsChild) {
        this.internalDependant.LifeEventProof = true;
      } else {
        this.internalDependant.LifeEventProof = false;
      }
      this.internalDependant = JSON.parse(
        JSON.stringify(this.internalDependant)
      );
      this.validate();
    },
    selectCoverageGroup(coverageGroupId, name, value) {
      if (value) {
        if (
          !this.coverageGroupsSelected.find((x) => x.Id === coverageGroupId)
        ) {
          const newGroup = {
            Id: coverageGroupId,
            Name: name,
            Proof: null,
          };
          this.coverageGroupsSelected.push(newGroup);
        }
      } else {
        this.coverageGroupsSelected = this.coverageGroupsSelected.filter(
          (x) => x.Id !== coverageGroupId
        );
      }
      this.$emit("update-coverages", this.coverageGroupsSelected);
    },
    openedSelect(isOpen) {
      if (isOpen) {
        this.selectingCoverage = true;
      } else {
        this.selectingCoverage = false;
      }
    },
  },
};
</script>
<style scoped>
.right-text-input >>> input {
  text-align: right;
}
.v-input--is-disabled {
  color: rgba(0, 0, 0, 0.87);
}
.chekbox-no-margin /deep/ .v-input--checkbox {
  margin-top: 0 !important;
}
.pricing-section {
  width: 100%;
}

.pricing-info-col {
  padding-right: 24px;
}

.pricing-line {
  margin-bottom: 8px;
  line-height: 1.4;
}

.pricing-variable-table {
  width: 100%;
}

.pricing-variable-table td {
  padding: 10px 16px !important;
  vertical-align: middle;
}

.pricing-coverage-name {
  min-width: 110px;
  white-space: nowrap;
}
</style>
